/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {useEffect, useState, useMemo} from 'react'
import {collection, doc, onSnapshot} from 'firebase/firestore'
import {useAuth} from '../../../modules/auth'
import {CloudPageTitle} from '../../../../_metronic/layout/core/CloudPageData'
import {Helmet} from 'react-helmet'
import db from '../../../../services/firebase'
import Searchbar from 'app/components/Searchbar'
import FormatTimestamp from 'app/components/FormatTimestamp'
import {Status} from '../../audit/components/Status'
import Overlay from 'app/components/Overlay'
import EcosystemIcon from 'app/components/EcosystemIcon'

const Assets: React.FC = () => {
  const [vulnerabilities, setVulnerabilities] = useState<any[]>([])
  const [searchItem, setSearchItem] = useState('')
  const [severityFilter, setSeverityFilter] = useState<string>('all')
  const [sourceFilter, setSourceFilter] = useState<string>('all')
  const {currentUser} = useAuth()

  useEffect(() => {
    getVendor()
  }, [])

  const getVendor = async () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const PolicyRef = collection(tenantRef, 'vulnerabilities')

      const unsubscribe = onSnapshot(PolicyRef, (snapshot) => {
        const risksData = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
          archived: doc.data().archived,
        }))
        setVulnerabilities(risksData)
      })

      return unsubscribe
    } catch (error) {
      console.error('Error getting controls:', error)
    }
  }

  const severityPriority = {
    critical: 1,
    high: 2,
    medium: 3,
    low: 4,
    info: 5,
  }

  const uniqueSources = useMemo(() => {
    const sources = vulnerabilities
      .map((vuln) => vuln.source)
      .filter((source) => source && typeof source === 'string')
      .filter((source, index, arr) => arr.indexOf(source) === index)
      .sort()

    return sources
  }, [vulnerabilities])

  const filteredAndSortedVulnerabilities = useMemo(() => {
    let filtered = vulnerabilities

    if (severityFilter !== 'all') {
      filtered = filtered.filter(
        (vuln) => vuln.severity?.toLowerCase() === severityFilter.toLowerCase()
      )
    }

    if (sourceFilter !== 'all') {
      filtered = filtered.filter((vuln) => vuln.source === sourceFilter)
    }

    if (searchItem) {
      filtered = filtered.filter(
        (vuln) =>
          vuln.package?.toLowerCase().includes(searchItem.toLowerCase()) ||
          vuln.summary?.toLowerCase().includes(searchItem.toLowerCase()) ||
          vuln.ecosystem?.toLowerCase().includes(searchItem.toLowerCase()) ||
          vuln.source?.toLowerCase().includes(searchItem.toLowerCase())
      )
    }

    return filtered.sort((a, b) => {
      const aPriority = severityPriority[a.severity?.toLowerCase()] || 999
      const bPriority = severityPriority[b.severity?.toLowerCase()] || 999

      if (aPriority !== bPriority) {
        return aPriority - bPriority
      }

      return (a.package || '').localeCompare(b.package || '')
    })
  }, [vulnerabilities, searchItem, severityFilter, sourceFilter])

  return (
    <>
      <Helmet>
        <title>SecureSlate | Clouds</title>
      </Helmet>
      <CloudPageTitle
        icon='Cloud.svg'
        title='Clouds'
        desc={`${vulnerabilities.length} vulnerabilities`}
      >
        Clouds
      </CloudPageTitle>

      <div className='card card-custom '>
        <div className='card-header border-0 pt-6 h-100'>
          <div className='card-title d-flex align-items-center justify-content-between w-100'>
            <Searchbar searchTerm={searchItem} setSearchTerm={setSearchItem} />

            <div className='d-flex align-items-center'>
              <select
                className='form-select form-select-sm w-auto fs-6'
                value={severityFilter}
                onChange={(e) => setSeverityFilter(e.target.value)}
                style={{minWidth: '120px', height: '40px'}}
              >
                <option value='all'>Severity</option>
                <option value='critical'>Critical</option>
                <option value='high'>High</option>
                <option value='medium'>Medium</option>
                <option value='low'>Low</option>
                <option value='info'>Info</option>
              </select>

              {uniqueSources.length > 1 && (
                <select
                  className='form-select form-select-sm w-auto fs-6 ms-3'
                  value={sourceFilter}
                  onChange={(e) => setSourceFilter(e.target.value)}
                  style={{minWidth: '120px', height: '40px'}}
                >
                  <option value='all'>Source</option>
                  {uniqueSources.map((source) => (
                    <option key={source} value={source}>
                      {source}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
        </div>

        <div className='card-body py-3'>
          <div className='table-responsive py-3 ' style={{minHeight: '50vh'}}>
            <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
              <thead>
                <tr className='fw-bold fs-7' style={{color: '#5A5A5A'}}>
                  <th className='ps-4 min-w-50px rounded-start'>TYPE</th>
                  <th className='min-w-150px'>NAME</th>
                  <th className='min-w-100px'>SEVERITY</th>
                  <th className='min-w-100px'>LOCATION</th>
                  <th className='min-w-15px'>SOURCE</th>
                  <th className='min-w-15px'>DISCOVERED AT</th>
                  <th className='min-w-50px'>STATUS</th>
                </tr>
              </thead>

              <tbody>
                {filteredAndSortedVulnerabilities.length > 0 ? (
                  filteredAndSortedVulnerabilities.map((vuln, index) => {
                    const repoName = vuln.repo_url?.split('/').pop() || ''
                    return (
                      <tr key={index}>
                        <td>
                          <div className='d-flex align-items-center ps-4'>
                            <EcosystemIcon ecosystem={vuln.ecosystem} />
                            <span className='text-capitalize'>{vuln.ecosystem}</span>
                          </div>
                        </td>
                        <td>
                          <div className='d-flex flex-column'>
                            {/* <a
                            href={vuln.url}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='text-dark fw-bold text-hover-primary'
                          > */}
                            <div className='d-flex align-items-center'>
                              <span className='text-capitalize fw-bold'>{vuln.package}</span>
                              {vuln.ecosystem === 'JS' && vuln.remediation && (
                                <Overlay content={vuln.remediation} placement='top' />
                              )}
                            </div>
                            {/* {vuln.package}
                          </a> */}
                            <small>{vuln.description}</small>
                          </div>
                        </td>
                        <td>
                          <Status status={vuln.severity} />
                        </td>
                        <td>
                          <div className='d-flex align-items-center'>{repoName ? repoName : '--'}</div>
                        </td>
                        <td>
                          <span className=''>{vuln.source}</span>
                        </td>
                        <td>{FormatTimestamp({timestamp: vuln.scan_date})}</td>
                        <td>
                          <Status status={vuln.status} />
                        </td>
                      </tr>
                    )
                  })
                ) : (
                  <tr>
                    <td colSpan={7} className='text-center'>
                      <div className='m-20'>
                        <img src='/media/buttons/emptystate.svg' alt='Empty State' />
                        <div className='m-5 fs-5'>
                          <p style={{fontWeight: 'bold'}}>No Vulnerabilities Found</p>
                        </div>
                        <p>
                          {searchItem || severityFilter !== 'all'
                            ? 'No vulnerabilities match your current filters. Try adjusting your search or severity filter.'
                            : 'No vulnerabilities detected in your asset.'}
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  )
}

export {Assets}
